import ROLES from 'constants/roles';
import { PERMISSIONS } from 'constants/permissions';
import { REVIEW_TYPES } from 'constants/review-types';
import { USER_OPTIONS } from 'constants/user-options';
import CreateProfileDialog from 'scenes/Dialog/components/CreateProfileDialog';
import ConfirmYourProfile from 'scenes/Dialog/components/ConfirmYourProfile';
import CandidateProfileNotApproved from 'scenes/Dialog/components/CandidateProfileNotApproved';

export const hasRole = (roles = [], value) => {
  return roles.some(role => {
    return role === value;
  });
};

export const isAuthCandidate = (user, id) => {
  return Number(user.cId) === Number(id);
};

export const Option = {
  has: (option, options) => {
    return typeof Option[options] === 'function'
      ? Option[option](options)
      : false;
  },

  isCandidateEmailConfirm: options => {
    return Object.keys(options).some(option => {
      return (
        option === USER_OPTIONS.IS_CANDIDATE_EMAIL_CONFIRM &&
        options[option] === true
      );
    });
  },

  isGmailInviteAvailable: options => {
    return Object.keys(options).some(option => {
      return (
        option === USER_OPTIONS.IS_GMAIL_INVITE_AVAILABLE &&
        options[option] === true
      );
    });
  },

  hasCandidateAccount: options => {
    return Object.keys(options).some(option => {
      return (
        option === USER_OPTIONS.HAS_CANDIDATE_ACCOUNT &&
        options[option] === true
      );
    });
  },

  hasResume: options => {
    return Object.keys(options).some(option => {
      return option === USER_OPTIONS.HAS_RESUME && options[option] === true;
    });
  },

  hasRecruiterCalendar: options => {
    return Object.keys(options).some(option => {
      return (
        option === USER_OPTIONS.HAS_RECRUITER_CALENDAR &&
        options[option] === true
      );
    });
  },
};

export const Permission = {
  isAllowed: (permission, permissions, bag) => {
    return typeof Permission[`isAllowed${permission}`] === 'function'
      ? Permission[`isAllowed${permission}`](permissions, bag)
      : false;
  },

  isAllowedMyTeam: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.MY_TEAM && permissions[permission] === true
      );
    });
  },

  isAllowedShortlist: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.SHORTLIST && permissions[permission] === true
      );
    });
  },

  isAllowedRated: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.RATED && permissions[permission] === true
      );
    });
  },

  isAllowedInBounds: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.INBOUNDS && permissions[permission] === true
      );
    });
  },

  isAllowedViewOwnProfile: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.VIEW_OWN_PROFILE &&
        permissions[permission] === true
      );
    });
  },

  isAllowedManageJobs: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.MANAGE_JOBS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedManageProjects: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.MANAGE_PROJECTS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedCandidatesShortlists: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.CANDIDATES_SHORTLISTS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedSubscriptionSearches: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.SUBSCRIPTION_SEARCHES &&
        permissions[permission] === true
      );
    });
  },

  isAllowedAddEmployeeReview: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.ADD_EMPLOYEE_REVIEW &&
        permissions[permission] === true
      );
    });
  },

  isAllowedAddOverviewReview: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.ADD_OVERVIEW_REVIEW &&
        permissions[permission] === true
      );
    });
  },

  isAllowedAddInterviewReview: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.ADD_INTERVIEW_REVIEW &&
        permissions[permission] === true
      );
    });
  },

  isAllowedAddReview: (permissions, type) => {
    if (type === REVIEW_TYPES.OVERVIEW || type === REVIEW_TYPES.COMPANY) {
      return Permission.isAllowedAddOverviewReview(permissions);
    } else if (type === REVIEW_TYPES.EMPLOYEE) {
      return Permission.isAllowedAddEmployeeReview(permissions);
    } else if (type === REVIEW_TYPES.INTERVIEW) {
      return Permission.isAllowedAddInterviewReview(permissions);
    }
    return false;
  },

  isAllowedSendMessageToCandidate: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.SEND_MESSAGE_TO_CANDIDATES &&
        permissions[permission] === true
      );
    });
  },

  isAllowedChangeCandidatesStatus: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.CHANGE_CANDIDATES_STATUS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedRateCandidates: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.RATED_CANDIDATES &&
        permissions[permission] === true
      );
    });
  },

  isAllowedPreparatoryFiles: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.PREPARATORY_FILES &&
        permissions[permission] === true
      );
    });
  },

  isAllowedViewPriceCandidatePremium: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.VIEW_PRICE_CANDIDATE_PREMIUM &&
        permissions[permission] === true
      );
    });
  },

  isAllowedPersonalCalendar: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.PERSONAL_CALENDAR &&
        permissions[permission] === true
      );
    });
  },

  isAllowedCreateCandidateAccount: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.CREATE_CANDIDATE_ACCOUNT &&
        permissions[permission] === true
      );
    });
  },

  isAllowedProcessViewApplicants: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.PROCESS_VIEW_APPLICANTS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedProjectViewApplicants: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.PROJECT_VIEW_APPLICANTS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedClosedSearch: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.CLOSED_SEARCH &&
        permissions[permission] === true
      );
    });
  },

  isAllowedCreateHiddenProcess: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.CREATE_HIDDEN_PROCESS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedEditCandidateProfile: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.EDIT_CANDIDATE_PROFILE &&
        permissions[permission] === true
      );
    });
  },

  isAllowedCandidateProfileAttachments: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.CANDIDATE_PROFILE_ATTACHMENTS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedRunAptitudeTest: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.RUN_APTITUDE_TEST &&
        permissions[permission] === true
      );
    });
  },

  isAllowedUseProcessSetupEmail: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.USE_PROCESS_SETUP_EMAIL &&
        permissions[permission] === true
      );
    });
  },

  isAllowedEditFirmProfile: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.EDIT_FIRM_PROFILE &&
        permissions[permission] === true
      );
    });
  },

  isAllowedChangeMultipleCandidatesStatus: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.CHANGE_MULTIPLE_CANDIDATES_STATUS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedSendCandidateNotify: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.SEND_CANDIDATE_NOTIFY &&
        permissions[permission] === true
      );
    });
  },

  isAllowedTeamProfileViews: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.TEAM_PROFILE_VIEWS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedUseProcessAllSearchesSort: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.USE_PROCESS_ALL_SEARCHES_SORT &&
        permissions[permission] === true
      );
    });
  },

  isAllowedUseNetworkFilters: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.USE_NETWORK_FILTERS &&
        permissions[permission] === true
      );
    });
  },

  isAllowedViewFirmEmployees: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.VIEW_FIRM_EMPLOYEES &&
        permissions[permission] === true
      );
    });
  },

  isAllowedViewGATPercentile: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.VIEW_GAT_PERCENTILE &&
        permissions[permission] === true
      );
    });
  },

  isAllowedViewProcessApplyRequirementsMismatch: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.VIEW_PROCESS_APPLY_REQUIREMENTS_MISMATCH &&
        permissions[permission] === true
      );
    });
  },

  isAllowedPostJob: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.POST_JOB && permissions[permission] === true
      );
    });
  },

  isAllowedPostProject: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.POST_PROJECT &&
        permissions[permission] === true
      );
    });
  },

  isAllowedUseEmailTemplateFollowup: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.USE_EMAIL_TEMPLATE_FOLLOWUP &&
        permissions[permission] === true
      );
    });
  },

  isAllowedDeleteCandidatePosition: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.DELETE_CANDIDATE_POSITION &&
        permissions[permission] === true
      );
    });
  },

  isAllowedMassSendJob: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.MASS_SEND_JOB &&
        permissions[permission] === true
      );
    });
  },

  isAllowedUseQuickFilters: permissions => {
    return Object.keys(permissions).some(permission => {
      return (
        permission === PERMISSIONS.USE_QUICK_FILTERS &&
        permissions[permission] === true
      );
    });
  },
};

export const Role = {
  isGuest: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.GUEST;
    });
  },

  isCandidate: (user, showDialog = false) => {
    const { roles, options } = user;

    if (
      roles.some(role => {
        return role === ROLES.CANDIDATE;
      })
    ) {
      return true;
    }

    if (showDialog) {
      const hasCandidateAccount = Option.hasCandidateAccount(options);
      const isCandidateEmailConfirm = Option.isCandidateEmailConfirm(options);

      if (!hasCandidateAccount) {
        CreateProfileDialog();
      } else if (hasCandidateAccount && !isCandidateEmailConfirm) {
        ConfirmYourProfile();
      } else {
        CandidateProfileNotApproved();
      }
    }

    return false;
  },

  isCandidatePremium: user => {
    return (
      Role.isCandidate(user, false) &&
      user.roles.some(role => {
        return (
          role === ROLES.CANDIDATE_PREMIUM_PREP ||
          role === ROLES.CANDIDATE_PREMIUM
        );
      })
    );
  },

  isCandidatePremiumWithPrep: user => {
    return (
      Role.isCandidate(user, false) &&
      user.roles.some(role => role === ROLES.CANDIDATE_PREMIUM_PREP)
    );
  },

  isCandidateWithRecruiterPosition: user => {
    return (
      Role.isCandidate(user, false) &&
      user.roles.some(role => role === ROLES.CANDIDATE_WITH_RECRUITER_POSITION)
    );
  },

  isAnonymous: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.ANONYMOUS;
    });
  },

  isRecruiter: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.RECRUITER;
    });
  },

  isRecruiterDefault: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.RECRUITER_DEFAULT;
    });
  },

  isRecruiterBasic: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.RECRUITER_BASIC;
    });
  },

  isRecruiterPremium: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.RECRUITER_PREMIUM;
    });
  },

  isRecruiterPremiumRecFirm: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.RECRUITER_PREMIUM_REC_FIRM;
    });
  },

  isRecruiterRestricted: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.RECRUITER_RESTRICTED;
    });
  },

  isRecruiterGbs: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.RECRUITER_GBS;
    });
  },

  isRecruiterGbsMasterAccount: user => {
    const { roles } = user;
    return roles.some(role => {
      return role === ROLES.RECRUITER_GBS_MASTER_ACCOUNT;
    });
  },
};

export const showNoCandidateProfileDialog = user => {
  const { options } = user;
  const hasCandidateAccount = Option.hasCandidateAccount(options);
  const isCandidateEmailConfirm = Option.isCandidateEmailConfirm(options);

  if (!hasCandidateAccount) {
    CreateProfileDialog();
  } else if (hasCandidateAccount && !isCandidateEmailConfirm) {
    ConfirmYourProfile();
  } else {
    CandidateProfileNotApproved();
  }
};
